import SavedBanner, { SavedBannerButton, SavedBannerLink } from '@/components/SavedBanner'
import { useEffect, useMemo, useRef, useState } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { openAfterAsync } from '@/lib/openAfterAsync'
import { ValidationError } from '@/lib/errorReporter'
import {
  CheckCircle, XCircle, RefreshCw, Shield, ClipboardCheck,
  Package, Send, AlertTriangle, Undo2, FileText, ChevronRight,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { storageFrom } from '@/lib/storage'
import PageHeader from '@/components/PageHeader'
import Modal from '@/components/Modal'
import { Field } from '@/components/Field'
import QueryStateGate, { type QueryStateLike } from '@/components/QueryStateGate'
import { useTenantFlows } from '@/lib/entityFlow'
import ModuleTabBar, { type ModuleTab } from '@/components/ModuleTabBar'
import MutationOverlays, { errorHandledElsewhere } from '@/components/MutationOverlays'
import { readState } from '@/lib/readState'
import ViewChipRow from '@/components/ViewChipRow'
import clsx from 'clsx'
import { useUrlState, oneOf } from '@/hooks/useUrlState'
import { useRegisterScroll, registerRow } from '@/hooks/useRegisterScroll'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { usePermissions } from '@/lib/usePermissions'
import { isManagerOrAbove, canSubmitPpecb, canAmendPpecb, ppecbPlatformBypass } from '@/lib/roles'
import { useIsPlatformAdmin } from '@/lib/useIsPlatformAdmin'
import { useSupportView } from '@/lib/platformView'
import StatusPill from '@/components/ui/StatusPill'
import VerifyButton from '@/components/VerifyButton'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import { promptDialog, alertDialog, confirmDialog } from '@/components/Dialogs'
import { askInspectorOnDuty, rememberInspectorOnDuty } from './inspection/inspectorOnDuty'
import { withTimeout } from '@/lib/saveTimeout'
import { qcHoldRefusal } from '@/lib/packhouseErrorDomains'
import QueueCard from './inspection/QueueCard'
import { PALLET_STATUS_LABEL } from '@/lib/palletStatus'
import { useHeldVerdicts, withHeld, heldOn } from './inspection/useHeldVerdicts'
import { useRunNames, useQcHolds, useTruckPlans, useInspectionDates } from './inspection/inspectionReads'
import ConsignmentPallets from './inspection/ConsignmentPallets'
import InspectionFind from './inspection/InspectionFind'
import { useCanOpen } from './inspection/useCanOpen'
import {
  APPROVED_FILTERS, approvedFilterSets, filterApproved, type ApprovedFilter,
  INSPECTION_VIEWS, splitQueue, viewCounts, countLabel, pipelineStrip, findInQueue, matchesFirst,
  isSubmittable, groupByMarket, sentLabel, splitQcHeld, ppecbSubmitError,
  type InspectionView, type ViewCounts, type SubmitRefusal,
} from './inspection/inspectionQueue'

/** The consignment's message exists on TITAN (a real one, not mock): the one
 *  condition under which Amend & revalidate is offered, and the card note names it. */
function hasTitanMessage(c: { inspection_message_id?: unknown; environment?: unknown }): boolean {
  return c.inspection_message_id != null && c.environment !== 'mock'
}

const SUBMIT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-ppecb-consignment`
const SIM_URL    = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ppecb-webhook-sim`
const UPDATE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-ppecb-consignment`
const REPORT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/download-ppecb-consignment-report`
/** UF-05: Approved cards drawn before the Show all (n) button. */
const APPROVED_FIRST = 50

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Pallet {
  pallet_id:            string
  status:               string
  // EXPORT vs LOCAL is on `pallets.channel`. Earlier code referred to it
  // as delivery_type — column doesn't exist on this table.
  channel:              'EXPORT' | 'LOCAL' | null
  grade:                string | null
  size_code:            string | null
  net_weight_kg:        number | null
  // Friendly variety name lives on `runs`, joined via run_id.
  runs:                 { variety: string | null } | null
  target_market:        string | null
  pallet_type:          string | null
  ppecb_consignment_id: string | null
  packed_at:            string | null
  stocked_at:           string | null
  coldroom_grid:        string | null
  run_id:               string | null
  ppecb_rejection_code: string | null
  ppecb_rejection_reason: string | null
  version:              number
}

interface ConsignmentRow {
  consignment_id:      string
  consignment_number:  string
  inspection_status:   string  // DRAFT | SUBMITTED | VALIDATED | INSPECTED | REJECTED
  verification_status: string  // PENDING | PASSED | FAILED
  submitted_at:        string
  inspection_completed_at: string | null
  pallet_count:        number
  target_market:       string | null
  environment:         string
  verified_at:         string | null   // mig 295 — admin sign-off
  // Mig 20260714130000 — PPECB amend/delete rail. A non-null
  // inspection_message_id means the message exists on TITAN (amendable;
  // recall does a real DELETE there); validation_errors are PPECB's named
  // field errors when validation failed.
  inspection_message_id: number | null
  validation_errors:     Array<{ property?: string; message?: string }> | null
}

interface RejectionReason {
  rejection_code:   string
  reason_type_name: string
  reason_name:      string
  synced_at:        string
}

function shortId(id: string): string {
  const parts = id.split('-')
  return parts.slice(-2).join('-')
}

// ---------------------------------------------------------------------------
// Reject Modal
// ---------------------------------------------------------------------------
interface RejectModalProps {
  pallet: Pick<Pallet, 'pallet_id' | 'grade' | 'size_code' | 'net_weight_kg'>
  reasons: RejectionReason[]
  /** The reasons read. When given and the PPECB list is empty, the select is replaced by the read's own
   *  loading / failed / empty state and Confirm stays off until the list is in (U19 D2). */
  reasonsQuery?: QueryStateLike
  onConfirm: (code: string, reason: string, notes: string) => void
  onCancel:  () => void
  isPending: boolean
  /** False on a business whose flows do not use PPECB: the wording drops "PPECB". Default true. */
  usesPpecb?: boolean
}

// A business with no PPECB flow has no PPECB-synced list, so it gets this short
// house list plus "Other" with a typed reason. A house reason is stored in the
// pallet's free-text `rejection_reason` ONLY: `ppecb_rejection_code` stays empty,
// because writing it fires the "PPECB rejected pallet" alert and non-conformance.
const HOUSE_REJECT_REASONS = [
  'Decay / rot',
  'Soft or overripe fruit',
  'Bruising or damage',
  'Foreign matter',
  'Under-weight',
  'Packaging or labelling fault',
  'Pest or disease',
] as const
const OTHER_REASON = '__other__'

/** Rejection reason: the PPECB list, or the house list on a no-PPECB flow. */
function RejectReasonField({ usesPpecb, houseList, listMissing, reasonsQuery, byType, selectedCode, onChange }: {
  usesPpecb: boolean
  houseList: boolean
  listMissing: boolean
  reasonsQuery: RejectModalProps['reasonsQuery']
  byType: Record<string, RejectionReason[]>
  selectedCode: string
  onChange: (code: string) => void
}) {
  const label = usesPpecb ? 'PPECB Rejection Reason' : 'Rejection reason'
  if (listMissing) {
    // Not inside <Field>: its <label> would swallow the Retry button's name and click.
    return (
      <div>
        <span className="label">{label} *</span>
        <QueryStateGate query={reasonsQuery as QueryStateLike} noun="the rejection reasons"
          empty={<p className="text-sm text-amber-300">No rejection reasons are set up. Ask your admin.</p>} />
      </div>
    )
  }
  return (
    <Field label={label} required>
      <select
        className="field"
        required
        value={selectedCode}
        onChange={e => onChange(e.target.value)}
      >
        <option value="">{houseList ? 'Select a reason…' : 'Select official reason…'}</option>
        {houseList ? (
          <>
            {HOUSE_REJECT_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
            <option value={OTHER_REASON}>Other</option>
          </>
        ) : Object.entries(byType).map(([type, items]) => (
          <optgroup key={type} label={type}>
            {items.map(r => (
              <option key={r.rejection_code} value={r.rejection_code}>
                [{r.rejection_code}] {r.reason_name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </Field>
  )
}

export function RejectModal({ pallet, reasons, reasonsQuery, onConfirm, onCancel, isPending, usesPpecb = true }: RejectModalProps) {
  const [selectedCode, setSelectedCode] = useState('')
  const [notes,        setNotes]        = useState('')
  const [otherText,    setOtherText]    = useState('')
  const houseList = !usesPpecb
  const isOther   = houseList && selectedCode === OTHER_REASON
  const listMissing = !houseList && !!reasonsQuery && reasons.length === 0

  // Group reasons by type
  const byType = reasons.reduce<Record<string, RejectionReason[]>>((acc, r) => {
    acc[r.reason_type_name] = [...(acc[r.reason_type_name] ?? []), r]
    return acc
  }, {})

  const selectedReason = reasons.find(r => r.rejection_code === selectedCode)

  // Dirty snapshot (W4.2 §4.2 — Modal wires it through useDirtyClose). An
  // inspector's chosen PPECB code and the notes explaining it ARE the record:
  // this sheet had no guard at all, so a stray tap sent both back to nothing
  // and the pallet's rejection had to be reasoned out a second time.
  const snapshot = JSON.stringify([selectedCode, notes, otherText])

  return (
    <Modal
      title={
        <span className="flex items-center gap-2">
          <XCircle size={18} className="text-red-400 shrink-0" />
          <span className="truncate">Record Rejection · Pallet {shortId(pallet.pallet_id)}</span>
        </span>
      }
      onClose={onCancel}
      snapshot={snapshot}
      onSubmit={() => houseList
        ? onConfirm('', isOther ? otherText.trim() : selectedCode, notes)
        : onConfirm(selectedCode, selectedReason?.reason_name ?? '', notes)}
      isPending={isPending}
      submitDisabled={listMissing}
      submitLabel="Confirm Rejection"
      submitPendingLabel="Recording…">
      <div className="space-y-4">
        {/* Pallet summary */}
        <div className="bg-surface rounded-xl p-3 text-sm space-y-1">
          <div className="flex gap-4">
            <span className="text-fg-muted">Grade</span>
            <span className="text-fg font-medium">{pallet.grade ?? '—'}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-fg-muted">Size</span>
            <span className="text-fg font-medium">{pallet.size_code ?? '—'}</span>
          </div>
          <div className="flex gap-4">
            <span className="text-fg-muted">Weight</span>
            <span className="text-fg font-medium">{pallet.net_weight_kg ? `${pallet.net_weight_kg} kg` : '—'}</span>
          </div>
        </div>

        <RejectReasonField
          usesPpecb={usesPpecb} houseList={houseList} listMissing={listMissing}
          reasonsQuery={reasonsQuery} byType={byType}
          selectedCode={selectedCode} onChange={setSelectedCode} />
        {selectedReason && !houseList && (
          <p className="text-xs text-amber-400 -mt-2">
            Code: <span className="font-mono font-bold">{selectedReason.rejection_code}</span>
            {' — '}{selectedReason.reason_type_name}
          </p>
        )}
        {isOther && (
          <Field label="Other reason" required>
            <input
              className="field"
              required
              placeholder="Type the reason"
              value={otherText}
              onChange={e => setOtherText(e.target.value)}
            />
          </Field>
        )}

        {/* Inspector notes */}
        <div>
          <label className="label">Inspector Notes (optional)</label>
          <textarea
            className="field resize-none"
            rows={2}
            placeholder={usesPpecb ? 'Additional observations from TITAN inspection…' : 'Additional observations from the inspection…'}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </div>

        {/* Warning */}
        <div className="bg-red-900/30 border border-red-800 rounded-xl px-3 py-2 text-xs text-red-300">
          Rejecting moves this pallet to <span className="font-bold">{PALLET_STATUS_LABEL.INSPECTED_REJECTED}</span>.
          It must then be sent to repack.
        </div>
      </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
// Result buttons for a submitted consignment. Mock → Sim PASS/FAIL (testing);
// staging + manual mode → Approve/Reject (records the real TITAN outcome via the
// admin/manager-gated applier); production → nothing. Own component so its
// branches don't inflate the consignment-list render. Mig 20260706210000.
// CRAP is inherent 3-state branching (hidden/mock/manual × 2 buttons) in a
// ~26-line leaf; splitting it into 3 functions to dodge the score would be worse.
// fallow-ignore-next-line complexity
function ConsignmentResultButtons({ c, manualResult, pending, onOutcome }: {
  c: ConsignmentRow
  manualResult: boolean
  pending: boolean
  onOutcome: (outcome: 'PASS' | 'FAIL') => void
}) {
  const isMock = c.environment === 'mock'
  if (!isMock && !(manualResult && c.environment === 'staging')) return null
  return (
    <div className="flex flex-col gap-1.5 mt-2">
      {!isMock && (
        <p className="text-caption text-tone-attention">Manual result — record the TITAN inspection outcome:</p>
      )}
      <div className="flex gap-2">
        <button onClick={() => onOutcome('PASS')} disabled={pending}
          className="rounded-lg px-3 py-1.5 text-xs bg-emerald-700 text-white font-semibold disabled:opacity-50">
          {isMock ? 'Sim PASS' : 'Approve'}
        </button>
        <button onClick={() => onOutcome('FAIL')} disabled={pending}
          className="rounded-lg px-3 py-1.5 text-xs bg-red-900/60 border border-red-700 text-red-200 font-semibold disabled:opacity-50">
          {isMock ? 'Sim FAIL' : 'Reject'}
        </button>
      </div>
    </div>
  )
}

type ChannelFilter = 'ALL' | 'EXPORT' | 'LOCAL'

/** What submit-ppecb-consignment answers (index.ts, the 200 bodies). */
interface SubmitResult { consignment_id?: string; consignment_number?: string; pallet_count?: number }

// To inspect with nothing on it says where the pallets went (UF-01): a pallet
// sent to PPECB or approved now sits on its own view, and "nothing to inspect"
// alone sends the clerk hunting for one that was approved an hour ago. Only
// ever rendered from QueryStateGate's `empty` branch, so the counts are read.
//
// UF-02: when the last card left during THIS visit, the empty state becomes
// the house done panel (SavedBanner). A page opened with nothing to inspect
// keeps today's plain state, and so does a Local / Export chip that hides
// pallets (its Show all button).
interface DoneFacts {
  /** Pallets given a verdict on this visit; 0 = the page opened empty. */
  decided: number
  approved: number
  /** Approved pallets with no truck; undefined while the truck read has not answered. */
  notOnTruck?: number
  canBuild: boolean
  upstream?: { coldroom: number; production: number }
  /** Where an upstream count opens Pallets, if this seat may open it. */
  palletsHref?: (status: 'IN_COLDROOM' | 'IN_PRODUCTION') => string
}

/** The detail lines under the done banner's title. */
function DoneDetails({ done }: { done: DoneFacts }) {
  const up = done.upstream
  const upLink = (status: 'IN_COLDROOM' | 'IN_PRODUCTION', n: number) => {
    const text = `${n} ${PALLET_STATUS_LABEL[status]}`
    return done.palletsHref
      ? <Link to={done.palletsHref(status)} className="underline underline-offset-2">{text}</Link>
      : <span>{text}</span>
  }
  return (
    <>
        {done.approved > 0 && (
          <p className="mt-0.5">
            You approved {done.approved} {done.approved === 1 ? 'pallet' : 'pallets'} this visit.
            {done.notOnTruck === undefined ? '' : done.notOnTruck === 0
              ? ' Every approved pallet is on a truck.'
              : ` ${done.notOnTruck} ${done.notOnTruck === 1 ? 'is' : 'are'} not on a truck yet.`}
          </p>
        )}
        {up && up.coldroom + up.production > 0 && (
          <p className="mt-0.5 text-xs" data-testid="done-upstream">
            Still upstream: {upLink('IN_COLDROOM', up.coldroom)} · {upLink('IN_PRODUCTION', up.production)}
          </p>
        )}
    </>
  )
}

/** UF-02: the done panel shown when the last card left during this visit. */
function InspectDonePanel({ done, counts, usesPpecb, onView }: {
  done: DoneFacts
  counts: ViewCounts
  usesPpecb: boolean
  onView: (v: InspectionView) => void
}) {
  return (
    <div data-testid="inspect-done">
      <SavedBanner title="All inspected"
        actions={<>
          {done.canBuild && counts.approved > 0 && (
            <SavedBannerLink to="/consignments?tab=build" data-testid="done-build-link">Build a consignment</SavedBannerLink>
          )}
          <SavedBannerButton onClick={() => onView('approved')}>Approved ({counts.approved})</SavedBannerButton>
          {usesPpecb && <SavedBannerButton onClick={() => onView('ppecb')}>PPECB ({counts.ppecb})</SavedBannerButton>}
        </>}>
        <DoneDetails done={done} />
      </SavedBanner>
    </div>
  )
}

/** Jump buttons under the empty state: the views that may still hold pallets. */
function EmptyViewChips({ counts, usesPpecb, onView }: {
  counts: ViewCounts
  usesPpecb: boolean
  onView: (v: InspectionView) => void
}) {
  return (
    <div className="flex gap-2 mt-4 flex-wrap justify-center">
      {/* Send to PPECB is a slice of To inspect, so it can only hold pallets
          here while a chip hides them (Local on, export pallets waiting). */}
      {usesPpecb && counts.send > 0 && (
        <button type="button" onClick={() => onView('send')} className="btn-secondary btn-sm min-h-[44px]">
          Send to PPECB ({counts.send})
        </button>
      )}
      {usesPpecb && (
        <button type="button" onClick={() => onView('ppecb')} className="btn-secondary btn-sm min-h-[44px]">
          With PPECB ({counts.ppecb})
        </button>
      )}
      <button type="button" onClick={() => onView('approved')} className="btn-secondary btn-sm min-h-[44px]">
        Approved ({counts.approved})
      </button>
    </div>
  )
}

function InspectEmpty({ filter, counts, onView, onAll, usesPpecb, done }: {
  filter: ChannelFilter
  counts: ViewCounts
  usesPpecb: boolean
  onView: (v: InspectionView) => void
  onAll:  () => void
  done?:  DoneFacts
}) {
  const kind = filter === 'EXPORT' ? 'export ' : filter === 'LOCAL' ? 'local ' : ''
  const showAll = filter !== 'ALL' && counts.inspect > 0
  if (done && done.decided > 0 && !showAll) {
    return <InspectDonePanel done={done} counts={counts} usesPpecb={usesPpecb} onView={onView} />
  }
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted text-center">
      <Package size={48} className="mb-3 opacity-40" aria-hidden />
      <p className="font-medium">No {kind}pallets to inspect</p>
      {showAll
        ? <button type="button" onClick={onAll} className="btn-ghost btn-sm min-h-[44px] mt-1">Show all {counts.inspect} to inspect</button>
        : <p className="text-sm mt-1">Move pallets to {PALLET_STATUS_LABEL.IN_LOADING_AREA} on the <Link to={`/pallets?status=IN_LOADING_AREA`} className="underline">Pallets page</Link></p>}
      <EmptyViewChips counts={counts} usesPpecb={usesPpecb} onView={onView} />
    </div>
  )
}

/** UF-03: one pipeline count as a house pill. The two upstream counts open the
 *  Pallets register on that status (44px tap target) when the viewer may open
 *  Pallets; the last one is this tab's own number and is never a link. */
function PipelinePill({ status, label, n, link }: { status: string; label: string; n: number; link: boolean }) {
  const pill = (
    <StatusPill object="pallet" status={status} label={label}
      icon={<span className="font-bold tabular-nums">{n}</span>} />
  )
  return link ? (
    <Link to={`/pallets?status=${status}`} aria-label={`${n} ${label}: open in Pallets`}
      className="inline-flex min-h-[44px] items-center">
      {pill}
    </Link>
  ) : pill
}

export default function InspectionPage() {
  const qc       = useQueryClient()
  const { data: me } = useCurrentUser()
  // Recall + delete: DELETE_ROLES (managers). Amend + re-inspect: AMEND_ROLES (UF-16).
  const canRecall    = isManagerOrAbove(me?.role)
  // A platform admin passes the edge functions' role check; a support view is read-only.
  const platformBypass = ppecbPlatformBypass(useIsPlatformAdmin(), useSupportView())
  const canAmend     = canAmendPpecb(me?.role, platformBypass)
  // submit-ppecb-consignment refuses a QC technician (SUBMIT_ROLES); UF-15.
  const canSubmit    = canSubmitPpecb(me?.role, platformBypass)
  const { data: activeTenant } = useActiveTenant()
  const tenantId = activeTenant?.tenantId ?? null

  // UF-08: the chip and the Find text live in the URL (elided at their defaults),
  // so Back from a pallet or a run keeps them; a plain /inspection opens clean.
  const [filterType,   setFilterType]   = useUrlState<ChannelFilter>('channel', 'ALL', oneOf('ALL', 'EXPORT', 'LOCAL'), { elide: true })
  // The Find box on To inspect (UF-02): a scanned label or the last digits.
  const [find,         setFind]         = useUrlState('find', '', undefined, { elide: true })
  const pageRef = useRef<HTMLDivElement>(null)
  // One view per state of the pallet (F03 UF-01), in the URL so Back and a
  // shared link keep it. A plain /inspection — Home's tile, the bottom bar, a
  // link without a view — opens To inspect: that is the first job of every
  // visit, so the old per-device memory of the mode is gone (a remembered
  // "Submitted" view hid the queue from the next inspector on that tablet).
  const [urlView, setView] = useUrlState<InspectionView>('view', 'inspect', oneOf(...INSPECTION_VIEWS), { elide: true })
  // F03 UF-17: a business whose flows do not use PPECB (berry, air freight)
  // gets To inspect and Approved only. Fail toward showing: ANY enabled flow
  // using PPECB (a mixed citrus + berry packhouse), or a lane list that could
  // not be read, keeps the PPECB views. A stale ?view=send|ppecb link on a
  // no-PPECB business lands on To inspect.
  const { manifests: flowManifests, flowsUnread, flowsLoading } = useTenantFlows()
  const usesPpecb = flowsUnread || flowsLoading || flowManifests.some(m => m.usesPPECB)
  const view: InspectionView = usesPpecb || urlView === 'inspect' || urlView === 'approved' ? urlView : 'inspect'
  // Tracks which pallet IDs in each target_market group are selected for
  // PPECB submission. Reset on group submit.
  const [submitSelection, setSubmitSelection] = useState<Set<string>>(new Set())
  const [submitErr, setSubmitErr] = useState<string | null>(null)
  // A submission refused for a QC FAIL: the selected pallets it holds, and
  // its market group, whose other ticked pallets can go on their own (UF-05).
  const [submitHeld, setSubmitHeld] = useState<{ market: string; held: string[] } | null>(null)
  // The last submission's tick, kept on Send to PPECB after the overlay's
  // moment: "Sent to PPECB as CN-… · 4 pallets · UK" with Open it (UF-04).
  const [sent, setSent] = useState<{ detail: string; consignmentNumber: string | null; consignmentId: string | null } | null>(null)
  // The consignment Open it pointed at: With PPECB scrolls to it and opens
  // its pallet list.
  const [focusCn, setFocusCn] = useState<string | null>(null)

  // Inspection page — one round-trip via mig 315 RPC. Replaces three
  // polls every 15s (queue + pipeline + consignments) with one poll every
  // 30s; also removes the 1000-row REST cap risk on the loading-area
  // queue. Server-side join hydrates pallet.run.variety + per-consignment
  // pallet count + target market in one shot.
  const dashboardQ = useQuery({
    queryKey: ['inspection-dashboard'],
    queryFn: async () => {
      const { data, error } = await withTimeout(supabase.rpc('get_inspection_dashboard'), 'Inspection: get_inspection_dashboard')
      if (error) throw error
      // The `?? []` guards below defend the FIELDS; this defends the object
      // they hang off. A NULL rpc result passes the cast untouched and then
      // `d.pallets` throws. <QueryStateGate query={dashboardQ}/> already
      // renders the honest version of this.
      if (data == null) throw new Error('get_inspection_dashboard returned no data')
      const d = data as { pallets: any[]; pipeline: Record<string, number>; consignments: any[] }
      return {
        pallets: (d.pallets ?? []).map(p => ({
          ...p,
          runs: { variety: p.run_variety ?? null },
        })) as Pallet[],
        pipeline: d.pipeline ?? { IN_PRODUCTION: 0, IN_COLDROOM: 0, IN_LOADING_AREA: 0 },
        consignments: (d.consignments ?? []) as ConsignmentRow[],
      }
    },
    refetchInterval: POLL.live,
  })
  const dashboard = dashboardQ.data
  const pallets:      Pallet[]         = dashboard?.pallets      ?? []
  const pipeline:     Record<string, number> | undefined = dashboard?.pipeline
  const consignments: ConsignmentRow[] = dashboard?.consignments ?? []

  // Result mode (mig 20260706210000): when this packhouse records PPECB results
  // by hand, submitted consignments get manual Approve/Reject (below) and the
  // poller skips them. Readable by any tenant member (packhouse_config tenant-read).
  const { data: manualResult = false } = useQuery({
    queryKey: ['ppecb-manual-result', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data } = await supabase
        .from('packhouse_config').select('ppecb_manual_result').eq('tenant_id', tenantId).maybeSingle()
      return !!(data as { ppecb_manual_result?: boolean } | null)?.ppecb_manual_result
    },
  })

  // Submit a target-market group of pallets to PPECB.
  const submitGroup = useMutation({
    mutationFn: async ({ palletIds, inspectorCode }: { palletIds: string[]; market: string; inspectorCode: string }): Promise<SubmitResult> => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new ValidationError('Not authenticated')
      const res = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ pallet_ids: palletIds, inspector_code: inspectorCode }),
      })
      const body = await res.json()
      if (!res.ok) throw ppecbSubmitError(res.status, body)
      return body as SubmitResult
    },
    onMutate: () => { setSent(null); setSubmitErr(null); setSubmitHeld(null) },
    onSuccess: (body, { palletIds, market, inspectorCode }) => {
      if (tenantId) rememberInspectorOnDuty(tenantId, inspectorCode)
      setSubmitSelection(new Set())
      setSent({
        // "4 pallets · UK": sentLabel minus its "Sent to PPECB [as CN-…]" head, which the banner shows as title + reference
        detail: sentLabel(body, palletIds.length, market).split(' · ').slice(1).join(' · '),
        consignmentNumber: body?.consignment_number ?? null,
        consignmentId: body?.consignment_id ?? null,
      })
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
    },
    // The red box on Send to PPECB answers a refusal with PPECB's own detail;
    // the overlay below shows pending and the tick only.
    onError: (e: SubmitRefusal, { palletIds, market }) => {
      // A QC-FAIL hold reads the plain sentence (QC_HOLD_DOMAIN); PPECB's own
      // refusals keep their text and detail.
      setSubmitErr(qcHoldRefusal(e) ?? e.message)
      if (e.blockingRuns?.length) {
        const runOf = new Map(pallets.map(p => [p.pallet_id, p.run_id]))
        const split = splitQcHeld(palletIds, runOf, e.blockingRuns)
        if (split.held.length > 0) setSubmitHeld({ market, held: split.held })
      }
    },
  })
  // "Untick these 2 and submit the rest": the held pallets leave the
  // selection, and the rest go as one submission. The rest is the group's
  // selection NOW, not at refusal time: a pallet ticked after the refusal goes
  // too, rather than being cleared from the selection unsent. The button shows
  // on the same rule, so a pallet ticked after an all-held refusal offers it.
  function restOfHeldGroup(): string[] {
    if (!submitHeld) return []
    const heldSet = new Set(submitHeld.held)
    const group = marketGroups.find(([m]) => m === submitHeld.market)?.[1] ?? []
    return group.map(p => p.pallet_id).filter(id => submitSelection.has(id) && !heldSet.has(id))
  }
  // Asks the inspector on duty BEFORE mutate(); null = cancelled, nothing is sent.
  async function sendGroup(palletIds: string[], market: string) {
    if (!tenantId) return
    const inspectorCode = await askInspectorOnDuty(null, tenantId)
    if (inspectorCode) submitGroup.mutate({ palletIds, market, inspectorCode })
  }
  function submitRest() {
    if (!submitHeld) return
    const { held, market } = submitHeld
    const rest = restOfHeldGroup()
    setSubmitSelection(prev => {
      const next = new Set(prev)
      for (const id of held) next.delete(id)
      return next
    })
    if (rest.length > 0) void sendGroup(rest, market)
    else { setSubmitErr(null); setSubmitHeld(null) }
  }
  const submitTick = sentLabel(submitGroup.data, submitGroup.variables?.palletIds.length ?? 0, submitGroup.variables?.market ?? null)

  // Bring the consignment Open it named into view once its card is on the
  // list (the dashboard refetch after a submit brings it), once per Open it.
  const scrolledTo = useRef<string | null>(null)
  function openConsignment(consignmentId: string) {
    scrolledTo.current = null
    setFocusCn(consignmentId)
    setView('ppecb')
  }
  useEffect(() => {
    if (view !== 'ppecb' || !focusCn || scrolledTo.current === focusCn) return
    const el = document.getElementById(`consignment-${focusCn}`)
    if (!el) return
    el.scrollIntoView({ block: 'start', behavior: 'smooth' })
    scrolledTo.current = focusCn
  })

  // Mock-mode webhook simulator — flips a consignment to PASS/FAIL.
  const sim = useMutation({
    mutationFn: async ({ consignmentId, outcome }: { consignmentId: string; outcome: 'PASS' | 'FAIL' }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new ValidationError('Not authenticated')
      // Authenticated by the caller's own JWT (Authorization below): the
      // edge function checks the user is an admin/manager of the
      // consignment's tenant and is disabled in production. No shared
      // secret — the old x-sim-secret shipped inside the JS bundle, which
      // let any bundle-reader flip any tenant's inspection.
      const res = await fetch(SIM_URL, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          Authorization:   `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ consignment_id: consignmentId, outcome }),
      })
      const body = await res.json()
      if (!res.ok) {
        if (res.status >= 400 && res.status < 500) {
          throw new ValidationError(body?.error ?? `HTTP ${res.status}`)
        }
        throw new Error(body?.error ?? `HTTP ${res.status}`)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
    },
    onError: (e: Error) => { void alertDialog({ title: 'Sim failed', message: e.message, tone: 'danger' }) },
  })

  // Shared caller for the update-ppecb-consignment edge function (PI guide
  // §3.3 amend / §3.4 delete). 4xx → ValidationError (operator-fixable);
  // PPECB's named validation errors are flattened into the message.
  // Error-body unwrapping is one chain of fallbacks (named errors → detail
  // → top-level), not splittable logic; mirrors submitGroup's unwrap above.
  // fallow-ignore-next-line complexity
  async function callUpdateConsignment(body: { consignment_id: string; action: 'amend' | 'delete' | 'reinspect'; reason?: string; inspector_code?: string }) {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) throw new ValidationError('Not authenticated')
    const res = await fetch(UPDATE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(body),
    })
    const resBody = await res.json()
    if (!res.ok) {
      const top = resBody?.error ?? `HTTP ${res.status}`
      const named = Array.isArray(resBody?.validation_errors)
        ? resBody.validation_errors.map((v: { property?: string; message?: string }) =>
            `• ${v.property ?? ''}: ${v.message ?? ''}`).join('\n')
        : null
      const detail = named ?? (resBody?.detail
        ? (typeof resBody.detail === 'string' ? resBody.detail : JSON.stringify(resBody.detail))
        : null)
      const msg = detail ? `${top}\n${detail}` : top
      if (res.status >= 400 && res.status < 500) throw new ValidationError(msg)
      throw new Error(msg)
    }
    return resBody
  }

  // Mig 290 → update-ppecb-consignment — admin/manager recall of a
  // SUBMITTED/FAILED consignment. Server-side + fail-closed: when the
  // message exists on TITAN the edge function DELETEs it there first
  // (PI guide §3.4) and only then marks it RECALLED + frees the pallets;
  // a PPECB refusal leaves everything untouched.
  const recall = useMutation({
    mutationFn: (vars: { consignmentId: string; reason: string }) =>
      callUpdateConsignment({ consignment_id: vars.consignmentId, action: 'delete', reason: vars.reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
    },
    onError: (e: Error) => { void alertDialog({ title: 'Recall failed', message: e.message, tone: 'danger' }) },
  })

  // Re-inspection (203) — re-presents a decided consignment against its
  // original UPN; PPECB issues a new inspection message and the row goes
  // back to awaiting a verdict (PI guide §3.5).
  const reinspect = useMutation({
    mutationFn: (v: { consignmentId: string; inspectorCode: string }) =>
      callUpdateConsignment({ consignment_id: v.consignmentId, action: 'reinspect', inspector_code: v.inspectorCode }),
    onSuccess: (body: { validation_message?: string }, v) => {
      if (tenantId) rememberInspectorOnDuty(tenantId, v.inspectorCode)
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
      void alertDialog({
        title:   'Re-inspection submitted',
        message: body?.validation_message ?? 'PPECB received the re-inspection — awaiting a fresh verdict.',
      })
    },
    onError: (e: Error) => {
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
      void alertDialog({ title: 'Re-inspection failed', message: e.message, tone: 'danger' })
    },
  })

  // Official consignment note / report PDF (PI guide §3.15). The edge fn
  // fetches + caches it in the ppecb-certs bucket; we then open a signed URL.
  const downloadReport = useMutation({
    mutationFn: async (consignmentId: string) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new ValidationError('Not authenticated')
      const res = await fetch(REPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ consignment_id: consignmentId }),
      })
      const resBody = await res.json()
      if (!res.ok) {
        const msg = resBody?.error ?? `HTTP ${res.status}`
        if (res.status >= 400 && res.status < 500) throw new ValidationError(msg)
        throw new Error(resBody?.detail ? `${msg}\n${String(resBody.detail).slice(0, 200)}` : msg)
      }
      const { data, error } = await storageFrom('ppecb-certs').createSignedUrl(resBody.path as string, 3600)
      if (error || !data?.signedUrl) throw new Error(error?.message ?? 'Could not create a download link')
      return data.signedUrl
    },
    // No window.open here: an open in onSuccess runs outside the tap's
    // gesture stack and iOS silently blocks it — the click handler claims
    // the window first via openAfterAsync (mobile audit 2026-07-19, S1).
    onError: (e: Error) => { void alertDialog({ title: 'Consignment note', message: e.message, tone: 'danger' }) },
  })

  // Amend in place — rebuilds the payload from the CURRENT pallet + config
  // data and PUTs it against the existing inspection message, which PPECB
  // revalidates synchronously. The fix-a-mistake path that doesn't burn the
  // consignment number or the pallet grouping.
  const amend = useMutation({
    mutationFn: (v: { consignmentId: string; inspectorCode: string }) =>
      callUpdateConsignment({ consignment_id: v.consignmentId, action: 'amend', inspector_code: v.inspectorCode }),
    onSuccess: (body: { validation_message?: string }, v) => {
      if (tenantId) rememberInspectorOnDuty(tenantId, v.inspectorCode)
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
      void alertDialog({
        title:   'Amend accepted',
        message: body?.validation_message ?? 'PPECB revalidated the consignment.',
      })
    },
    onError: (e: Error) => {
      qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
      void alertDialog({ title: 'Amend failed', message: e.message, tone: 'danger' })
    },
  })

  // PPECB rejection reasons
  const reasonsQ = useQuery({
    queryKey: ['ppecb-rejection-reasons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ppecb_rejection_reasons')
        .select('*')
        .order('reason_type_name')
        .order('reason_name')
      if (error) throw error
      return data as RejectionReason[]
    },
  })
  const reasons = reasonsQ.data ?? []

  // Every queue row on its view, each oldest clock first (a pallet sent
  // straight to the loading bay, with no cold-room stamp, sorts by when it was
  // packed). Approve / Reject confirm on the card (F03 UF-02): a card whose
  // verdict was just recorded stays on the view it was tapped on to say so —
  // an approved pallet has moved to Approved, a rejected one has left the
  // queue read altogether.
  const split = splitQueue(pallets)
  // An Approved card is released once its time is up AND this read no longer
  // lists the pallet where it was tapped (useHeldVerdicts).
  const stillQueued = useMemo(() => {
    const s = splitQueue(pallets)
    return new Set([...s.inspect, ...s.ppecb].map(p => p.pallet_id))
  }, [pallets])
  const { held, hold, release, approvedThisVisit, decidedThisVisit } = useHeldVerdicts<Pallet>(stillQueued)
  const toInspect = withHeld(split.inspect, heldOn(held, 'inspect'))
  // What the Find box names comes to the top — even past a chip that would
  // hide it: a scan is a request for THAT pallet. The rest keep their order.
  const found     = findInQueue(toInspect, find)
  const foundSet  = new Set(found)
  const shown     = matchesFirst(
    toInspect.filter(p => foundSet.has(p.pallet_id) || filterType === 'ALL' || p.channel === filterType),
    found,
  )
  const withPpecb = withHeld(split.ppecb, heldOn(held, 'ppecb'))
  // Each consignment card lists its own queue pallets (UF-04).
  const byConsignment = new Map<string, Pallet[]>()
  for (const p of withPpecb) {
    if (!p.ppecb_consignment_id) continue
    const list = byConsignment.get(p.ppecb_consignment_id)
    if (list) list.push(p)
    else byConsignment.set(p.ppecb_consignment_id, [p])
  }
  // Each card names its run and grower; a pallet or run number links only
  // where this viewer's role may open it (UF-04). One read for every view, so
  // switching tabs never re-asks.
  const queueRunIds = [...pallets, ...[...held.values()].map(h => h.pallet)].map(p => p.run_id)
  const runNames = useRunNames(queueRunIds).data
  // A run's unreleased QC FAIL shows on its cards before anyone taps Approve
  // (UF-05). Unread or failed = no flag: the approve trigger still refuses.
  const qcHolds  = useQcHolds(queueRunIds).data
  // UF-13: which approved pallets are planned on a truck. Runs after the list
  // paints; unread or failed = the card says nothing about a truck.
  const truckPlansQ = useTruckPlans(
    [...split.approved, ...[...held.values()].filter(h => h.outcome === 'APPROVED').map(h => h.pallet)].map(p => p.pallet_id),
  )
  const truckPlans = truckPlansQ.data
  // UF-15: the re-inspect clock on an Approved card. Unread = no chip.
  const inspectionDatesQ = useInspectionDates(
    [...split.approved, ...[...held.values()].filter(h => h.outcome === 'APPROVED').map(h => h.pallet)].map(p => p.pallet_id),
  )
  const inspectionDates = inspectionDatesQ.data
  // UF-04: the Approved chips. In the URL (?show=) so Back and a shared link
  // keep it; All is the default and leaves the URL clean.
  const [approvedShow, setApprovedShow] = useUrlState<ApprovedFilter>('show', 'all', oneOf(...APPROVED_FILTERS), { elide: true })
  const canOpenPallet = useCanOpen('pallets')
  const canOpenRun    = useCanOpen('runs')
  const canOpenQc     = useCanOpen('qc')
  const canOpenCompliance = useCanOpen('compliance')
  const canOpenConsignments = useCanOpen('consignments')
  const { can: canQcPerm } = usePermissions()
  const canReleaseQc  = canQcPerm('qc', 'manage')

  // Each tab, the chips and the header count from ONE read, and only once it
  // has answered: a dash when it could not be read (a dash is not a zero —
  // this is the PPECB queue, and a false zero reads as "nothing is waiting"),
  // and nothing while the first read is in flight, where the page used to
  // print "0 pallets awaiting inspection · All (0)". The header also stopped
  // counting approved pallets as "awaiting".
  const queueState = readState(dashboardQ)
  const counts     = viewCounts(pallets)
  const count      = (n: number) => countLabel(queueState, n)
  // UF-08: Back puts the list at the card the inspector left; a fresh arrival starts at the top.
  useRegisterScroll({ key: 'inspection', ready: queueState === 'usable', rootRef: pageRef })
  // D3: the strip's "ready to inspect" is the To inspect tab's number.
  const pipelineNums = pipeline ? pipelineStrip(pipeline, pallets) : undefined
  // UF-02: facts for the done panel. Not on a truck: counted only when the
  // truck read answered for EVERY approved pallet; an unread pallet is not
  // "not on a truck". Build a consignment follows the server's gate (D3).
  const notOnTruck = truckPlans && split.approved.length > 0 && split.approved.every(p => truckPlans.has(p.pallet_id))
    ? split.approved.filter(p => { const t = truckPlans.get(p.pallet_id); return !!t && !t.consignment && !t.mission }).length
    : undefined
  const filterSets = approvedFilterSets(split.approved, truckPlans, inspectionDates)
  // A count is a number once its read answered for every pallet, "…" while it
  // is in flight and "—" when it could not be read: never a 0 for either.
  const chipFor = (set: Set<string> | undefined, q: Parameters<typeof readState>[0]) =>
    set ? String(set.size) : readState(q) === 'loading' ? '…' : '—'
  // UF-05: a busy packhouse holds hundreds of approved cards; draw the first
  // 50 and let Show all (n) draw the rest. Chip counts above stay whole-list.
  const [approvedAll, setApprovedAll] = useState(false)
  const approvedShown = filterApproved(split.approved, approvedShow, filterSets)
  const doneFacts: DoneFacts = {
    decided: decidedThisVisit,
    approved: approvedThisVisit,
    notOnTruck,
    canBuild: canOpenConsignments && canQcPerm('consignments', 'dispatch'),
    upstream: pipelineNums ? { coldroom: pipelineNums.IN_COLDROOM, production: pipelineNums.IN_PRODUCTION } : undefined,
    palletsHref: canOpenPallet ? s => `/pallets?status=${s}` : undefined,
  }
  const chipCount  = (n: number) => { const c = count(n); return c === undefined ? '' : ` (${c})` }
  // Opens on Send when pallets wait to be sent, else on With PPECB.
  const openPpecbTab = () => setView(counts.send > 0 ? 'send' : 'ppecb')
  const tabs: ReadonlyArray<ModuleTab<InspectionView>> = [
    { key: 'inspect',  label: 'To inspect',    icon: ClipboardCheck, count: count(counts.inspect)  },
    // UF-11: Send and With PPECB are one tab; it carries no count (Send is a slice of To inspect), the chips under the bar carry both numbers.
    ...(usesPpecb ? [
      { key: 'ppecb' as const, label: 'PPECB', icon: Send },
    ] : []),
    { key: 'approved', label: 'Approved',      icon: CheckCircle,    count: count(counts.approved) },
  ]

  // Export pallets that can go to PPECB, one group per market — PPECB
  // requires one market per submission, so each group is a discrete outbound
  // consignment. Only IN_LOADING_AREA pallets not already on a consignment
  // (isSubmittable).
  const marketGroups = groupByMarket(split.inspect.filter(isSubmittable))

  const card = (p: Pallet) => (
    <QueueCard
      key={p.pallet_id}
      pallet={p}
      truck={truckPlans?.get(p.pallet_id)}
      inspection={inspectionDates?.get(p.pallet_id)}
      run={p.run_id ? runNames?.get(p.run_id) : undefined}
      canOpenPallet={canOpenPallet}
      canOpenRun={canOpenRun}
      qcHold={p.run_id ? qcHolds?.get(p.run_id) : undefined}
      canOpenQc={canOpenQc}
      canOpenCompliance={canOpenCompliance}
      canRelease={canReleaseQc}
      rejectSheet={s => <RejectModal pallet={p} reasons={reasons} reasonsQuery={reasonsQ} usesPpecb={usesPpecb} {...s} />}
      held={held.get(p.pallet_id)?.outcome}
      consignmentStatus={consignments.find(c => c.consignment_id === p.ppecb_consignment_id)?.inspection_status}
      canRecall={canRecall}
      canAmend={canAmend}
      onTitan={(() => { const c = consignments.find(k => k.consignment_id === p.ppecb_consignment_id); return !!c && hasTitanMessage(c) })()}
      onVerdict={hold}
      onDismissRejected={() => release(p.pallet_id)}
    />
  )

  function toggleSelect(id: string) {
    setSubmitSelection(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  function selectAllInGroup(group: Pallet[]) {
    setSubmitSelection(prev => {
      const next = new Set(prev)
      for (const p of group) next.add(p.pallet_id)
      return next
    })
  }
  function clearGroupSelection(group: Pallet[]) {
    setSubmitSelection(prev => {
      const next = new Set(prev)
      for (const p of group) next.delete(p.pallet_id)
      return next
    })
  }

  return (
    <div ref={pageRef} className="min-h-full bg-canvas flex flex-col">
      {/* Header */}
      <PageHeader title={usesPpecb ? 'PPECB Inspection' : 'Inspection'} icon={<Shield size={18} className="text-blue-400" />}
        subtitle={
          queueState === 'unread'
            ? <span className="text-amber-300">Couldn't read the inspection queue — the counts below are not zeros</span>
            : undefined
        } />

      {/* One view per state of the pallet (UF-01). The strip owns its own
          scroller; this wrapper only draws the rule under it. */}
      <div className="border-b border-line-subtle px-2">
        <ModuleTabBar tabs={tabs}
          value={view === 'send' ? 'ppecb' : view}
          onChange={k => (k === 'ppecb' ? openPpecbTab() : setView(k))} />
      </div>
      {(view === 'send' || view === 'ppecb') && (
        <div className="px-4 pt-3" data-testid="ppecb-views">
          <ViewChipRow<'send' | 'ppecb'> label="PPECB queue" value={view} onChange={setView}
            options={[
              { key: 'send',  label: 'Send',        count: count(counts.send) },
              { key: 'ppecb', label: 'With PPECB',  count: count(counts.ppecb) },
            ]} />
        </div>
      )}

      {view === 'inspect' && (
        <>
          {/* Find the pallet in front of you (UF-02). */}
          <div className="px-4 pt-3">
            <InspectionFind query={find} onQuery={setFind} toInspect={toInspect} queue={pallets}
              queueState={queueState} onView={setView} canOpenPallet={canOpenPallet} />
          </div>

          {/* All / Export / Local filter the cards right below them. */}
          <div className="flex gap-3 px-4 py-3 border-b border-line-subtle">
            <button
              type="button"
              onClick={() => setFilterType('ALL')}
              aria-pressed={filterType === 'ALL'}
              className={clsx(
                'flex-1 rounded-xl min-h-[44px] py-2.5 text-center text-sm font-semibold transition-colors',
                filterType === 'ALL' ? 'bg-raised text-fg' : 'bg-surface text-fg-muted'
              )}
            >
              All{chipCount(split.inspect.length)}
            </button>
            <button
              type="button"
              onClick={() => setFilterType('EXPORT')}
              aria-pressed={filterType === 'EXPORT'}
              className={clsx(
                'flex-1 rounded-xl min-h-[44px] py-2.5 text-center text-sm font-semibold transition-colors',
                filterType === 'EXPORT' ? 'bg-raised text-fg' : 'bg-surface text-fg-muted'
              )}
            >
              Export{chipCount(split.inspect.filter(p => p.channel === 'EXPORT').length)}
            </button>
            <button
              type="button"
              onClick={() => setFilterType('LOCAL')}
              aria-pressed={filterType === 'LOCAL'}
              className={clsx(
                'flex-1 rounded-xl min-h-[44px] py-2.5 text-center text-sm font-semibold transition-colors',
                filterType === 'LOCAL' ? 'bg-raised text-fg' : 'bg-surface text-fg-muted'
              )}
            >
              Local{chipCount(split.inspect.filter(p => p.channel === 'LOCAL').length)}
            </button>
          </div>

          {/* Pipeline strip — surfaces upstream pallets so an empty inspection
              list is self-explanatory. */}
          {pipeline && pipelineNums && (
            <div className="px-4 py-2 border-b border-line-subtle bg-canvas/40 flex flex-wrap gap-x-2 items-center text-caption">
              <span className="text-fg-muted uppercase tracking-wider font-semibold">Pipeline:</span>
              <PipelinePill status="IN_PRODUCTION" label="In Production" n={pipelineNums.IN_PRODUCTION} link={canOpenPallet} />
              <span className="text-fg-muted">→</span>
              <PipelinePill status="IN_COLDROOM" label="In Coldroom" n={pipelineNums.IN_COLDROOM} link={canOpenPallet} />
              <span className="text-fg-muted">→</span>
              <PipelinePill status="IN_LOADING_AREA" label="ready to inspect" n={pipelineNums.IN_LOADING_AREA} link={false} />
            </div>
          )}

          <div className="flex-1 p-4 space-y-3 pb-8">
            {/* Gate the empty branch — a failed/paused dashboard load must
                never read as "nothing to inspect" (idle-wake audit UI-4). */}
            {shown.length === 0 && (
              <QueryStateGate query={dashboardQ} noun="pallets"
                empty={<InspectEmpty filter={filterType} counts={counts} usesPpecb={usesPpecb} onView={setView} onAll={() => setFilterType('ALL')} done={doneFacts} />} />
            )}
            {/* Always wrapped, ring or not, so a card never remounts — and
                drops its pending Approve — when the box changes. */}
            {shown.map(p => (
              <div key={p.pallet_id} {...registerRow(p.pallet_id)} data-found={foundSet.has(p.pallet_id) || undefined}
                className={clsx('rounded-2xl', foundSet.has(p.pallet_id) && 'ring-2 ring-sky-400')}>
                {card(p)}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Send to PPECB — export pallets grouped by market. PPECB requires one
          market per submission so each market gets its own group. */}
      {view === 'send' && (
        <div className="flex-1 p-4 space-y-3 pb-8">
          {sent && (
            <SavedBanner title="Sent to PPECB" reference={sent.consignmentNumber ?? undefined} onDismiss={() => setSent(null)}
              actions={sent.consignmentId ? (
                <SavedBannerButton onClick={() => openConsignment(sent.consignmentId!)}>Open it</SavedBannerButton>
              ) : undefined}>
              <p className="text-xs mt-0.5">{sent.detail}</p>
            </SavedBanner>
          )}
          {marketGroups.length === 0 && (
            <QueryStateGate query={dashboardQ} noun="pallets"
              empty={
                <div className="flex flex-col items-center justify-center py-12 text-muted text-center">
                  <Send size={48} className="mb-3 opacity-40" aria-hidden />
                  <p className="font-medium">No export pallets to send to PPECB</p>
                  <p className="text-sm mt-1">An export pallet shows here once it is in the loading area with a target market, and leaves when it is sent.</p>
                </div>
              } />
          )}
          {marketGroups.length > 0 && (
            <Card className="p-3 space-y-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-blue-300 font-semibold flex items-center gap-1.5">
                  <Send size={12} /> Submit export pallets to PPECB
                </p>
                <p className="text-caption text-fg-muted mt-0.5">
                  Pick one market group at a time. PPECB requires single-market submissions.
                </p>
              </div>
              {marketGroups.map(([market, group]) => {
                const selected = group.filter(p => submitSelection.has(p.pallet_id))
                const allSelected = selected.length === group.length && group.length > 0
                return (
                  <div key={market} className="bg-canvas/60 border border-line rounded-lg p-2.5 space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => allSelected ? clearGroupSelection(group) : selectAllInGroup(group)}
                          className="min-w-[44px] min-h-[44px] -m-2.5 flex items-center justify-center shrink-0">
                          <span className={clsx(
                            'w-5 h-5 rounded border-2 flex items-center justify-center',
                            allSelected ? 'border-blue-500 bg-blue-500' :
                            selected.length > 0 ? 'border-blue-500 bg-blue-500/30' : 'border-line-strong',
                          )}>
                            {allSelected && <CheckCircle size={12} className="text-white" />}
                          </span>
                        </button>
                        <span className="text-sm font-bold text-fg font-mono">{market}</span>
                        <span className="text-caption text-fg-muted">
                          {selected.length} of {group.length} selected
                        </span>
                      </div>
                      {canSubmit ? (
                        <button
                          onClick={() => void sendGroup(selected.map(p => p.pallet_id), market)}
                          disabled={selected.length === 0 || submitGroup.isPending}
                          className="btn-primary btn-sm flex items-center gap-1.5 disabled:opacity-50">
                          <Send size={12} />
                          Submit {selected.length || ''}
                        </button>
                      ) : me ? (
                        <span className="text-caption text-fg-muted">{"An inspector or manager sends pallets to PPECB. A dispatcher sends them from the truck's PPECB consignment panel in Dispatch."}</span>
                      ) : null}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                      {group.map(p => {
                        const checked = submitSelection.has(p.pallet_id)
                        return (
                          <button key={p.pallet_id}
                            onClick={() => toggleSelect(p.pallet_id)}
                            className={clsx(
                              'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors',
                              checked ? 'bg-blue-900/40 ring-1 ring-blue-600' : 'bg-surface/50 hover:bg-surface',
                            )}>
                            <div className={clsx(
                              'w-4 h-4 rounded border flex items-center justify-center shrink-0',
                              checked ? 'border-blue-500 bg-blue-500' : 'border-line-strong',
                            )}>
                              {checked && <CheckCircle size={10} className="text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-mono text-fg truncate">{p.pallet_id}</p>
                              <p className="text-caption text-fg-muted truncate">
                                {p.runs?.variety ?? '—'}{p.grade ? ` · ${p.grade}` : ''}{p.size_code ? ` · ${p.size_code}` : ''}
                              </p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </Card>
          )}
          {submitErr && (
            <div className="rounded-lg bg-red-900/40 border border-red-700 px-3 py-2 text-xs text-red-300 flex items-start gap-2">
              <AlertTriangle size={12} className="shrink-0 mt-0.5" />
              <span className="flex-1 min-w-0 space-y-2">
                <span className="block whitespace-pre-wrap break-words">{submitErr}</span>
                {submitHeld && (
                  <>
                    <span className="block text-red-200 break-words">
                      Held by QC: <span className="font-mono">{submitHeld.held.join(', ')}</span>
                    </span>
                    {restOfHeldGroup().length > 0 && (
                      <button type="button" onClick={submitRest} disabled={submitGroup.isPending}
                        className="btn-secondary btn-sm min-h-[44px] disabled:opacity-50">
                        Untick {submitHeld.held.length === 1 ? 'this 1' : `these ${submitHeld.held.length}`} and submit the rest
                      </button>
                    )}
                  </>
                )}
              </span>
              <button onClick={() => { setSubmitErr(null); setSubmitHeld(null) }} className="text-red-300 hover:text-red-100">✕</button>
            </div>
          )}
        </div>
      )}

      {/* With PPECB — the consignments sent to PPECB, and the pallets on them
          that are still in the loading area while TITAN decides. */}
      {view === 'ppecb' && (
        <div className="flex-1 p-4 space-y-2 pb-8">
          {/* Gate, not a bare claim: pending/paused/error rendered "No PPECB submissions yet" as truth (idle-wake audit UI-4). */}
          {consignments.length === 0 && withPpecb.length === 0 && (
            <QueryStateGate query={dashboardQ} noun="PPECB submissions"
              empty={
                <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center">
                  <Send size={48} className="mb-3 opacity-40" aria-hidden />
                  <p className="font-medium">No PPECB submissions yet</p>
                  <p className="text-sm mt-1">Send a market group from Send to PPECB.</p>
                </div>
              } />
          )}
          {
          // Consignment card — presentational status branching (tone / chip /
          // result / amend / recall per status), JSX conditionals not logic;
          // no component-test rig to buy the coverage down.
          // fallow-ignore-next-line complexity
          consignments.map(c => {
            const inspected = c.inspection_status === 'INSPECTED'
            const rejected  = c.inspection_status === 'REJECTED'
            const recalled  = c.inspection_status === 'RECALLED'
            const submitted = !inspected && !rejected && !recalled
            const tone = inspected ? 'border-emerald-700 bg-emerald-900/20'
                       : rejected  ? 'border-red-700 bg-red-900/20'
                       : recalled  ? 'border-line-strong bg-surface/40 opacity-70'
                                   : 'border-amber-700/50 bg-amber-900/10'
            return (
              <div key={c.consignment_id} id={`consignment-${c.consignment_id}`} {...registerRow(c.consignment_id)}
                className={clsx('card border-2 scroll-mt-4', tone, focusCn === c.consignment_id && 'ring-2 ring-blue-500')}>
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <p className="font-mono font-bold text-fg">{c.consignment_number}</p>
                  <div className="flex items-center gap-2">
                    <VerifyButton
                      table="ppecb_consignments"
                      rowId={c.consignment_id}
                      verifiedAt={c.verified_at}
                      compact
                    />
                    <StatusPill object="ppecb_consignment" status={c.inspection_status} />
                  </div>
                </div>
                <p className="text-xs text-fg-muted mt-1">
                  Market <span className="text-fg font-mono">{c.target_market ?? '—'}</span>
                  {' · '}
                  <span className="text-fg font-semibold">{c.pallet_count}</span> pallets
                  {' · '}
                  Submitted {new Date(c.submitted_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                  {c.environment !== 'mock' ? '' : ' · mock'}
                </p>
                {submitted && (
                  <ConsignmentResultButtons c={c} manualResult={manualResult} pending={sim.isPending}
                    onOutcome={(outcome) => sim.mutate({ consignmentId: c.consignment_id, outcome })} />
                )}
                {/* Official PPECB consignment note (PI guide §3.15) — exists
                    once TITAN records an outcome; cached in ppecb-certs.
                    Re-inspection (203, §3.5) re-presents the fruit against
                    the original UPN when it ages out, a market changes, or
                    a rejection is re-presented after rework. */}
                {(inspected || rejected) && c.environment !== 'mock' && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <button
                      onClick={() => { openAfterAsync(() => downloadReport.mutateAsync(c.consignment_id)).catch(() => { /* surfaced by the mutation's onError dialog */ }) }}
                      disabled={downloadReport.isPending}
                      title="Download PPECB's official consignment note / report PDF"
                      className="rounded-lg px-3 min-h-[44px] text-xs bg-raised hover:bg-raised-hover text-blue-200 font-semibold disabled:opacity-50 flex items-center gap-1.5">
                      {/* One mutation serves every card — scope the busy label
                          to the card actually clicked (mutation.variables). */}
                      <FileText size={12} /> {downloadReport.isPending && downloadReport.variables === c.consignment_id ? 'Fetching…' : 'Consignment note (PDF)'}
                    </button>
                    {canAmend && (
                      <Button variant="secondary" size="sm"
                        onClick={async () => {
                          const ok = await confirmDialog({
                            title:   `Re-present ${c.consignment_number} for inspection?`,
                            message: 'Submits a re-inspection (203) to PPECB against the original UPN — use when fruit aged past its window, the market changed, or a rejection is re-presented after rework. The consignment goes back to awaiting a fresh verdict.',
                            confirm: 'Submit re-inspection',
                            tone:    'warning',
                          })
                          const inspectorCode = ok && tenantId ? await askInspectorOnDuty(null, tenantId) : null
                          if (inspectorCode) reinspect.mutate({ consignmentId: c.consignment_id, inspectorCode })
                        }}
                        disabled={reinspect.isPending}
                        title="Submit a re-inspection (203) against the original UPN"
                        icon={<RefreshCw size={12} />}>
                        {reinspect.isPending && reinspect.variables?.consignmentId === c.consignment_id ? 'Submitting…' : 'Re-inspection (203)'}
                      </Button>
                    )}
                  </div>
                )}
                {/* Its pallets (UF-04), each a link; the ones still on the
                    queue keep their card, with no Approve / Reject (UF-20): recall first.
                    Then PPECB's named validation errors on a FAILED
                    submission — the fields to fix before amending — each
                    naming its pallet when PPECB's text quotes it. */}
                <ConsignmentPallets
                  consignmentId={c.consignment_id}
                  palletCount={c.pallet_count}
                  onQueue={byConsignment.get(c.consignment_id) ?? []}
                  card={card}
                  canOpenPallet={canOpenPallet}
                  defaultOpen={submitted || focusCn === c.consignment_id || byConsignment.has(c.consignment_id)}
                  errors={submitted ? c.validation_errors : null}
                />
                {/* Amend/recall rail (update-ppecb-consignment, PI guide
                    §3.3/§3.4). Amend = fix-in-place: PUTs the current pallet
                    + config data against the existing TITAN message, which
                    revalidates synchronously. Recall = DELETE on TITAN (when
                    the message exists there) then free the pallets. */}
                {submitted && canAmend && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {hasTitanMessage(c) && (
                      <Button variant="primary" size="sm"
                        onClick={async () => {
                          const ok = await confirmDialog({
                            title:   `Amend consignment ${c.consignment_number}?`,
                            message: 'Resends the consignment to PPECB with the CURRENT pallet + config data against the same inspection message — use after fixing the fields PPECB flagged. TITAN revalidates immediately.',
                            confirm: 'Amend & revalidate',
                            tone:    'warning',
                          })
                          const inspectorCode = ok && tenantId ? await askInspectorOnDuty(null, tenantId) : null
                          if (inspectorCode) amend.mutate({ consignmentId: c.consignment_id, inspectorCode })
                        }}
                        disabled={amend.isPending}
                        title="Resubmit current data against the existing TITAN message (PUT)"
                        icon={<RefreshCw size={12} />}>
                        {amend.isPending && amend.variables?.consignmentId === c.consignment_id ? 'Amending…' : 'Amend & revalidate'}
                      </Button>
                    )}
                    {canRecall && (
                    <Button variant="secondary" size="sm"
                      onClick={async () => {
                        const onTitan = hasTitanMessage(c)
                        const reason = await promptDialog({
                          title:       `Recall consignment ${c.consignment_number}?`,
                          message:     `This frees the ${c.pallet_count} pallets back to the inspection queue so you can re-group + resubmit.\n\n${onTitan ? 'The inspection message is DELETED on TITAN first — if PPECB refuses (already being processed), nothing changes locally.' : 'This consignment never reached PPECB, so the recall is local only.'}`,
                          placeholder: 'Reason (saved on the audit trail)',
                          confirm:     'Recall',
                          tone:        'warning',
                        })
                        if (reason && reason.trim()) {
                          recall.mutate({ consignmentId: c.consignment_id, reason: reason.trim() })
                        }
                      }}
                      disabled={recall.isPending}
                      title="Recall (deletes the TITAN message when one exists, then frees pallets back to inspection)"
                      icon={<Undo2 size={12} />}>
                      {recall.isPending && recall.variables?.consignmentId === c.consignment_id ? 'Recalling…' : 'Recall (mistake)'}
                    </Button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Approved — pallets that passed, waiting for a truck (they leave the
          queue read when their truck is dispatched). */}
      {view === 'approved' && (
        <div className="flex-1 p-4 space-y-3 pb-8">
          {/* D3: the next step for an approved pallet is the Build pool. The link
              follows the server's gate (consignments:dispatch); any other seat
              reads who builds trucks. */}
          {!doneFacts.canBuild && split.approved.length > 0 && (
            <p className="text-sm text-muted" data-testid="approved-build-note">
              A manager or dispatcher builds trucks from these pallets.
            </p>
          )}
          {doneFacts.canBuild && split.approved.length > 0 && (
            <Link to="/consignments?tab=build" data-testid="approved-build-link"
              className="inline-flex items-center gap-1 min-h-[44px] font-semibold text-blue-300 underline underline-offset-2">
              Build a consignment <ChevronRight size={16} aria-hidden />
            </Link>
          )}
          {split.approved.length === 0 && (
            <QueryStateGate query={dashboardQ} noun="pallets"
              empty={
                <div className="flex flex-col items-center justify-center py-12 text-muted text-center">
                  <CheckCircle size={48} className="mb-3 opacity-40" aria-hidden />
                  <p className="font-medium">No approved pallets waiting for a truck</p>
                  <p className="text-sm mt-1">An approved pallet stays here until its truck is dispatched.</p>
                </div>
              } />
          )}
          {split.approved.length > 0 && (
            <ViewChipRow label="Show approved pallets" value={approvedShow} onChange={setApprovedShow}
              options={[
                { key: 'all',     label: 'All',            count: String(split.approved.length) },
                { key: 'notruck', label: 'Not on a truck', count: chipFor(filterSets.notruck, truckPlansQ) },
                { key: 'near',    label: 'Near the limit', count: chipFor(filterSets.near, inspectionDatesQ) },
              ]} />
          )}
          {approvedShow !== 'all' && split.approved.length > 0 && approvedShown.length === 0 && (
            <p data-testid="approved-filter-empty" className="text-sm text-muted py-4">
              No approved pallets match. Tap <b>All</b> to see every approved pallet.
            </p>
          )}
          {approvedShown.slice(0, approvedAll ? undefined : APPROVED_FIRST).map(p => <div key={p.pallet_id} {...registerRow(p.pallet_id)}>{card(p)}</div>)}
          {!approvedAll && approvedShown.length > APPROVED_FIRST && (
            <Button variant="secondary" fullWidth data-testid="approved-show-all" onClick={() => setApprovedAll(true)}>
              Show all ({approvedShown.length})
            </Button>
          )}
        </div>
      )}

      {/* The submit's pending card and its tick, naming the consignment; at
          the page root so the tick survives the group leaving Send to PPECB.
          A refusal is the red box's to answer (PPECB's own detail). */}
      <MutationOverlays of={[[errorHandledElsewhere(submitGroup), submitTick]]} />
    </div>
  )
}
